export { categories, categoryRecord, coreCategoryDescriptions, coreCategories, isDataType, moduleCategories } from '../src/builtin/interface'
export type { Category, DataType } from '../src/builtin/interface'

function getNumberTheorySequenceNames<T extends string>(name: T): [`numberTheory.${T}Seq`, `numberTheory.${T}Nth`, `numberTheory.${T}TakeWhile`, `numberTheory.is${Capitalize<T>}`] {
  return [`numberTheory.${name}Seq`, `numberTheory.${name}Nth`, `numberTheory.${name}TakeWhile`, `numberTheory.is${name.charAt(0).toUpperCase()}${name.slice(1)}` as `numberTheory.is${Capitalize<T>}`]
}

function getVectorReductionNames<T extends string>(name: T): [`vector.${T}`, `vector.moving${Capitalize<T>}`, `vector.centeredMoving${Capitalize<T>}`, `vector.running${Capitalize<T>}`] {
  const capName = name.charAt(0).toUpperCase() + name.slice(1)
  return [`vector.${name}`, `vector.moving${capName}`, `vector.centeredMoving${capName}`, `vector.running${capName}`] as [`vector.${T}`, `vector.moving${Capitalize<T>}`, `vector.centeredMoving${Capitalize<T>}`, `vector.running${Capitalize<T>}`]
}

function getVectorMovingNames<T extends string>(name: T): [`vector.moving-${string}`, `vector.centered-moving-${string}`, `vector.running-${string}`] {
  return [`vector.moving-${name}`, `vector.centered-moving-${name}`, `vector.running-${name}`]
}

export const api = {
  collection: [
    'filter',
    'map',
    'reduce',
    'count',
    'get',
    'contains',
    'assoc',
    '++',
  ] as const,
  collectionUtils: [
    'collection.filteri',
    'collection.mapi',
    'collection.reducei',
    'collection.reduceRight',
    'collection.reduceiRight',
    'collection.reductions',
    'collection.reductionsi',
    'collection.getIn',
    'collection.assocIn',
    'collection.update',
    'collection.updateIn',
    'collection.notEmpty',
    'collection.isEvery',
    'collection.notEvery',
    'collection.isAny',
    'collection.notAny',
  ] as const,
  array: [
    'range',
    'repeat',
    'flatten',
  ] as const,
  sequence: [
    'nth',
    'push',
    'pop',
    'indexOf',
    'some',
    'reverse',
    'first',
    'second',
    'last',
    'rest',
    'next',
    'sort',
    'slice',
    'take',
    'takeLast',
    'drop',
    'dropLast',
    'takeWhile',
    'dropWhile',
  ] as const,
  sequenceUtils: [
    'sequence.mapcat',
    'sequence.position',
    'sequence.lastIndexOf',
    'sequence.unshift',
    'sequence.splice',
    'sequence.sortBy',
    'sequence.distinct',
    'sequence.remove',
    'sequence.removeAt',
    'sequence.splitAt',
    'sequence.splitWith',
    'sequence.frequencies',
    'sequence.groupBy',
    'sequence.partition',
    'sequence.partitionAll',
    'sequence.partitionBy',
    'sequence.isStartsWith',
    'sequence.isEndsWith',
    'sequence.interleave',
    'sequence.interpose',
  ] as const,
  math: [
    '+',
    '-',
    '*',
    '/',
    'mod',
    '%',
    'quot',
    'inc',
    'dec',
    'sqrt',
    'cbrt',
    '^',
    'round',
    'trunc',
    'floor',
    'ceil',
    'min',
    'max',
    'abs',
    'sign',
  ] as const,
  mathUtils: [
    'math.ln',
    'math.log2',
    'math.log10',
    'math.sin',
    'math.cos',
    'math.tan',
    'math.asin',
    'math.acos',
    'math.atan',
    'math.sinh',
    'math.cosh',
    'math.tanh',
    'math.asinh',
    'math.acosh',
    'math.atanh',
    'math.toRad',
    'math.toDeg',
  ] as const,
  functional: [
    '|>',
    'apply',
    'identity',
    'comp',
    'constantly',
  ] as const,
  functionalUtils: [
    'functional.juxt',
    'functional.complement',
    'functional.everyPred',
    'functional.somePred',
    'functional.fnull',
  ] as const,
  meta: [
    'doc',
    'arity',
    'effectName',
    'qualifiedName',
    'qualifiedMatcher',
  ] as const,
  misc: [
    '!=',
    '==',
    '<',
    '>',
    '<=',
    '>=',
    'not',
    'boolean',
    'compare',
    'typeOf',
    'raise',
  ] as const,
  object: [
    'dissoc',
    'keys',
    'vals',
    'entries',
    'find',
    'merge',
    'mergeWith',
    'zipmap',
    'selectKeys',
  ] as const,
  predicate: [
    'isBoolean',
    'isNull',
    'isNumber',
    'isString',
    'isFunction',
    'isInteger',
    'isArray',
    'isObject',
    'isCollection',
    'isSequence',
    'isRegexp',
    'isEffect',
    'isZero',
    'isPos',
    'isNeg',
    'isEven',
    'isOdd',
    'isFalse',
    'isTrue',
    'isEmpty',
    'isNotEmpty',
    'isVector',
    'isGrid',
    'isMatrix',
  ] as const,
  regularExpression: [
    'regexp',
    'reMatch',
    'replace',
    'replaceAll',
  ] as const,
  string: [
    'str',
    'number',
    'lowerCase',
    'upperCase',
    'trim',
    'split',
    'join',
    'isBlank',
  ] as const,
  stringUtils: [
    'string.stringRepeat',
    'string.fromCharCode',
    'string.toCharCode',
    'string.trimLeft',
    'string.trimRight',
    'string.padLeft',
    'string.padRight',
    'string.splitLines',
    'string.template',
    'string.encodeBase64',
    'string.decodeBase64',
    'string.encodeUriComponent',
    'string.decodeUriComponent',
    'string.capitalize',
  ] as const,
  bitwise: [
    '<<',
    '>>',
    '>>>',
    '&',
    '|',
    'xor',
  ] as const,
  bitwiseUtils: [
    'bitwise.bitNot',
    'bitwise.bitAndNot',
    'bitwise.bitFlip',
    'bitwise.bitClear',
    'bitwise.bitSet',
    'bitwise.bitTest',
  ] as const,
  assertion: [
    'assert',
  ] as const,
  assertionUtils: [
    'assertion.assertEqual',
    'assertion.assertNotEqual',
    'assertion.assertGt',
    'assertion.assertLt',
    'assertion.assertGte',
    'assertion.assertLte',
    'assertion.assertTrue',
    'assertion.assertFalse',
    'assertion.assertTruthy',
    'assertion.assertFalsy',
    'assertion.assertNull',
    'assertion.assertFails',
    'assertion.assertFailsWith',
    'assertion.assertSucceeds',
    'assertion.assertArray',
    'assertion.assertBoolean',
    'assertion.assertCollection',
    'assertion.assertFunction',
    'assertion.assertGrid',
    'assertion.assertInteger',
    'assertion.assertMatrix',
    'assertion.assertNumber',
    'assertion.assertObject',
    'assertion.assertRegexp',
    'assertion.assertSequence',
    'assertion.assertString',
    'assertion.assertVector',
  ] as const,
  grid: [
    'grid.isCellEvery',
    'grid.isSome',
    'grid.isEveryRow',
    'grid.isSomeRow',
    'grid.isEveryCol',
    'grid.isSomeCol',
    'grid.row',
    'grid.col',
    'grid.shape',
    'grid.fill',
    'grid.generate',
    'grid.reshape',
    'grid.transpose',
    'grid.flipH',
    'grid.flipV',
    'grid.rotate',
    'grid.crop',
    'grid.sliceRows',
    'grid.sliceCols',
    'grid.spliceRows',
    'grid.spliceCols',
    'grid.concatRows',
    'grid.concatCols',
    'grid.cellMap',
    'grid.cellMapi',
    'grid.cellReduce',
    'grid.cellReducei',
    'grid.pushRows',
    'grid.unshiftRows',
    'grid.popRow',
    'grid.shiftRow',
    'grid.pushCols',
    'grid.unshiftCols',
    'grid.popCol',
    'grid.shiftCol',
    'grid.fromArray',
  ] as const,
  matrix: [
    'matrix.mul',
    'matrix.det',
    'matrix.inv',
    'matrix.adj',
    'matrix.cofactor',
    'matrix.minor',
    'matrix.trace',
    'matrix.isSymmetric',
    'matrix.isTriangular',
    'matrix.isUpperTriangular',
    'matrix.isLowerTriangular',
    'matrix.isDiagonal',
    'matrix.isSquare',
    'matrix.isOrthogonalMatrix',
    'matrix.isIdentity',
    'matrix.isInvertible',
    'matrix.hilbert',
    'matrix.vandermonde',
    'matrix.band',
    'matrix.isBanded',
    'matrix.rank',
    'matrix.frobeniusNorm',
    'matrix.oneNorm',
    'matrix.infNorm',
    'matrix.maxNorm',
  ] as const,
  vectorUtils: [
    'vector.movingFn',
    'vector.runningFn',
    'vector.sum',
    'vector.prod',
    'vector.mean',
    'vector.median',
    'vector.isMonotonic',
    'vector.isStrictlyMonotonic',
    'vector.isIncreasing',
    'vector.isDecreasing',
    'vector.isStrictlyIncreasing',
    'vector.isStrictlyDecreasing',
    'vector.mode',
    'vector.minIndex',
    'vector.maxIndex',
    'vector.sortIndices',
    'vector.countValues',
    'vector.linspace',
    'vector.cumsum',
    'vector.cumprod',
    'vector.quartiles',
    'vector.percentile',
    'vector.quantile',
    'vector.histogram',
    'vector.ecdf',
    'vector.isOutliers',
    'vector.outliers',
    'vector.bincount',
    'vector.winsorize',
    'vector.mse',
    'vector.mae',
    'vector.rmse',
    'vector.smape',
    ...getVectorMovingNames('mean'),
    ...getVectorMovingNames('median'),
    ...getVectorReductionNames('variance'),
    ...getVectorReductionNames('sampleVariance'),
    ...getVectorMovingNames('sum'),
    ...getVectorMovingNames('prod'),
    ...getVectorReductionNames('stdev'),
    ...getVectorReductionNames('sampleStdev'),
    ...getVectorReductionNames('iqr'),
    ...getVectorReductionNames('span'),
    ...getVectorReductionNames('geometricMean'),
    ...getVectorReductionNames('harmonicMean'),
    ...getVectorReductionNames('skewness'),
    ...getVectorReductionNames('sampleSkewness'),
    ...getVectorReductionNames('kurtosis'),
    ...getVectorReductionNames('sampleKurtosis'),
    ...getVectorReductionNames('excessKurtosis'),
    ...getVectorReductionNames('sampleExcessKurtosis'),
    ...getVectorReductionNames('rms'),
    ...getVectorReductionNames('mad'),
    ...getVectorReductionNames('medad'),
    ...getVectorReductionNames('giniCoefficient'),
    ...getVectorReductionNames('entropy'),
    ...getVectorReductionNames('skewness'),
  ] as const,
  linAlg: [
    'linearAlgebra.reflect',
    'linearAlgebra.refract',
    'linearAlgebra.lerp',
    'linearAlgebra.rotate2d',
    'linearAlgebra.rotate3d',
    'linearAlgebra.dot',
    'linearAlgebra.cross',
    'linearAlgebra.normalizeMinmax',
    'linearAlgebra.normalizeZscore',
    'linearAlgebra.normalizeRobust',
    'linearAlgebra.normalizeL1',
    'linearAlgebra.normalizeL2',
    'linearAlgebra.normalizeLog',
    'linearAlgebra.angle',
    'linearAlgebra.projection',
    'linearAlgebra.isOrthogonal',
    'linearAlgebra.isParallel',
    'linearAlgebra.isCollinear',
    'linearAlgebra.cosineSimilarity',
    'linearAlgebra.euclideanDistance',
    'linearAlgebra.euclideanNorm',
    'linearAlgebra.manhattanDistance',
    'linearAlgebra.manhattanNorm',
    'linearAlgebra.hammingDistance',
    'linearAlgebra.hammingNorm',
    'linearAlgebra.chebyshevDistance',
    'linearAlgebra.chebyshevNorm',
    'linearAlgebra.minkowskiDistance',
    'linearAlgebra.minkowskiNorm',
    'linearAlgebra.cov',
    'linearAlgebra.corr',
    'linearAlgebra.spearmanCorr',
    'linearAlgebra.pearsonCorr',
    'linearAlgebra.kendallTau',
    'linearAlgebra.autocorrelation',
    'linearAlgebra.crossCorrelation',
    'linearAlgebra.rref',
    'linearAlgebra.solve',
    'linearAlgebra.toPolar',
    'linearAlgebra.fromPolar',
  ] as const,
  numberTheory: [
    ...getNumberTheorySequenceNames('abundant'),
    ...getNumberTheorySequenceNames('arithmetic'),
    ...getNumberTheorySequenceNames('bell'),
    ...getNumberTheorySequenceNames('catalan'),
    ...getNumberTheorySequenceNames('composite'),
    ...getNumberTheorySequenceNames('deficient'),
    ...getNumberTheorySequenceNames('factorial'),
    ...getNumberTheorySequenceNames('fibonacci'),
    ...getNumberTheorySequenceNames('geometric'),
    ...getNumberTheorySequenceNames('golomb'),
    ...getNumberTheorySequenceNames('happy'),
    ...getNumberTheorySequenceNames('lookAndSay'),
    ...getNumberTheorySequenceNames('lucas'),
    ...getNumberTheorySequenceNames('lucky'),
    ...getNumberTheorySequenceNames('mersenne'),
    ...getNumberTheorySequenceNames('padovan'),
    ...getNumberTheorySequenceNames('partition'),
    ...getNumberTheorySequenceNames('pell'),
    ...getNumberTheorySequenceNames('perfect'),
    ...getNumberTheorySequenceNames('perfectCube'),
    ...getNumberTheorySequenceNames('perfectPower'),
    ...getNumberTheorySequenceNames('perfectSquare'),
    ...getNumberTheorySequenceNames('polygonal'),
    ...getNumberTheorySequenceNames('prime'),
    ...getNumberTheorySequenceNames('recaman'),
    ...getNumberTheorySequenceNames('sylvester'),
    ...getNumberTheorySequenceNames('thueMorse'),
    ...getNumberTheorySequenceNames('tribonacci'),
    'numberTheory.collatzSeq',
    'numberTheory.jugglerSeq',
    'numberTheory.bernoulliSeq',
    'numberTheory.bernoulliTakeWhile',
    'numberTheory.bernoulliNth',
    'numberTheory.combinations',
    'numberTheory.countCombinations',
    'numberTheory.derangements',
    'numberTheory.countDerangements',
    'numberTheory.divisors',
    'numberTheory.countDivisors',
    'numberTheory.properDivisors',
    'numberTheory.countProperDivisors',
    'numberTheory.primeFactors',
    'numberTheory.countPrimeFactors',
    'numberTheory.distinctPrimeFactors',
    'numberTheory.countDistinctPrimeFactors',
    'numberTheory.factorial',
    'numberTheory.partitions',
    'numberTheory.countPartitions',
    'numberTheory.permutations',
    'numberTheory.countPermutations',
    'numberTheory.powerSet',
    'numberTheory.countPowerSet',
    'numberTheory.isCoprime',
    'numberTheory.isDivisibleBy',
    'numberTheory.gcd',
    'numberTheory.lcm',
    'numberTheory.multinomial',
    'numberTheory.isAmicable',
    'numberTheory.eulerTotient',
    'numberTheory.mobius',
    'numberTheory.mertens',
    'numberTheory.sigma',
    'numberTheory.carmichaelLambda',
    'numberTheory.cartesianProduct',
    'numberTheory.perfectPower',
    'numberTheory.modExp',
    'numberTheory.modInv',
    'numberTheory.extendedGcd',
    'numberTheory.chineseRemainder',
    'numberTheory.stirlingFirst',
    'numberTheory.stirlingSecond',
  ] as const,
  convert: [
    'convert.angstromToUm',
    'convert.angstromToMm',
    'convert.angstromToCm',
    'convert.angstromToM',
    'convert.angstromToKm',
    'convert.angstromToIn',
    'convert.angstromToFt',
    'convert.angstromToYd',
    'convert.angstromToMi',
    'convert.angstromToNmi',
    'convert.umToAngstrom',
    'convert.umToMm',
    'convert.umToCm',
    'convert.umToM',
    'convert.umToKm',
    'convert.umToIn',
    'convert.umToFt',
    'convert.umToYd',
    'convert.umToMi',
    'convert.umToNmi',
    'convert.mmToAngstrom',
    'convert.mmToUm',
    'convert.mmToCm',
    'convert.mmToM',
    'convert.mmToKm',
    'convert.mmToIn',
    'convert.mmToFt',
    'convert.mmToYd',
    'convert.mmToMi',
    'convert.mmToNmi',
    'convert.cmToAngstrom',
    'convert.cmToUm',
    'convert.cmToMm',
    'convert.cmToM',
    'convert.cmToKm',
    'convert.cmToIn',
    'convert.cmToFt',
    'convert.cmToYd',
    'convert.cmToMi',
    'convert.cmToNmi',
    'convert.mToAngstrom',
    'convert.mToUm',
    'convert.mToMm',
    'convert.mToCm',
    'convert.mToKm',
    'convert.mToIn',
    'convert.mToFt',
    'convert.mToYd',
    'convert.mToMi',
    'convert.mToNmi',
    'convert.kmToAngstrom',
    'convert.kmToUm',
    'convert.kmToMm',
    'convert.kmToCm',
    'convert.kmToM',
    'convert.kmToIn',
    'convert.kmToFt',
    'convert.kmToYd',
    'convert.kmToMi',
    'convert.kmToNmi',
    'convert.inToAngstrom',
    'convert.inToUm',
    'convert.inToMm',
    'convert.inToCm',
    'convert.inToM',
    'convert.inToKm',
    'convert.inToFt',
    'convert.inToYd',
    'convert.inToMi',
    'convert.inToNmi',
    'convert.ftToAngstrom',
    'convert.ftToUm',
    'convert.ftToMm',
    'convert.ftToCm',
    'convert.ftToM',
    'convert.ftToKm',
    'convert.ftToIn',
    'convert.ftToYd',
    'convert.ftToMi',
    'convert.ftToNmi',
    'convert.ydToAngstrom',
    'convert.ydToUm',
    'convert.ydToMm',
    'convert.ydToCm',
    'convert.ydToM',
    'convert.ydToKm',
    'convert.ydToIn',
    'convert.ydToFt',
    'convert.ydToMi',
    'convert.ydToNmi',
    'convert.miToAngstrom',
    'convert.miToUm',
    'convert.miToMm',
    'convert.miToCm',
    'convert.miToM',
    'convert.miToKm',
    'convert.miToIn',
    'convert.miToFt',
    'convert.miToYd',
    'convert.miToNmi',
    'convert.nmiToAngstrom',
    'convert.nmiToUm',
    'convert.nmiToMm',
    'convert.nmiToCm',
    'convert.nmiToM',
    'convert.nmiToKm',
    'convert.nmiToIn',
    'convert.nmiToFt',
    'convert.nmiToYd',
    'convert.nmiToMi',
    'convert.mgToG',
    'convert.mgToKg',
    'convert.mgToT',
    'convert.mgToOz',
    'convert.mgToLb',
    'convert.mgToSt',
    'convert.gToMg',
    'convert.gToKg',
    'convert.gToT',
    'convert.gToOz',
    'convert.gToLb',
    'convert.gToSt',
    'convert.kgToMg',
    'convert.kgToG',
    'convert.kgToT',
    'convert.kgToOz',
    'convert.kgToLb',
    'convert.kgToSt',
    'convert.tToMg',
    'convert.tToG',
    'convert.tToKg',
    'convert.tToOz',
    'convert.tToLb',
    'convert.tToSt',
    'convert.ozToMg',
    'convert.ozToG',
    'convert.ozToKg',
    'convert.ozToT',
    'convert.ozToLb',
    'convert.ozToSt',
    'convert.lbToMg',
    'convert.lbToG',
    'convert.lbToKg',
    'convert.lbToT',
    'convert.lbToOz',
    'convert.lbToSt',
    'convert.stToMg',
    'convert.stToG',
    'convert.stToKg',
    'convert.stToT',
    'convert.stToOz',
    'convert.stToLb',
    'convert.mlToCl',
    'convert.mlToDl',
    'convert.mlToL',
    'convert.mlToTsp',
    'convert.mlToTbsp',
    'convert.mlToFlOz',
    'convert.mlToCup',
    'convert.mlToPt',
    'convert.mlToQt',
    'convert.mlToGal',
    'convert.clToMl',
    'convert.clToDl',
    'convert.clToL',
    'convert.clToTsp',
    'convert.clToTbsp',
    'convert.clToFlOz',
    'convert.clToCup',
    'convert.clToPt',
    'convert.clToQt',
    'convert.clToGal',
    'convert.dlToMl',
    'convert.dlToCl',
    'convert.dlToL',
    'convert.dlToTsp',
    'convert.dlToTbsp',
    'convert.dlToFlOz',
    'convert.dlToCup',
    'convert.dlToPt',
    'convert.dlToQt',
    'convert.dlToGal',
    'convert.lToMl',
    'convert.lToCl',
    'convert.lToDl',
    'convert.lToTsp',
    'convert.lToTbsp',
    'convert.lToFlOz',
    'convert.lToCup',
    'convert.lToPt',
    'convert.lToQt',
    'convert.lToGal',
    'convert.tspToMl',
    'convert.tspToCl',
    'convert.tspToDl',
    'convert.tspToL',
    'convert.tspToTbsp',
    'convert.tspToFlOz',
    'convert.tspToCup',
    'convert.tspToPt',
    'convert.tspToQt',
    'convert.tspToGal',
    'convert.tbspToMl',
    'convert.tbspToCl',
    'convert.tbspToDl',
    'convert.tbspToL',
    'convert.tbspToTsp',
    'convert.tbspToFlOz',
    'convert.tbspToCup',
    'convert.tbspToPt',
    'convert.tbspToQt',
    'convert.tbspToGal',
    'convert.fl-ozToMl',
    'convert.fl-ozToCl',
    'convert.fl-ozToDl',
    'convert.fl-ozToL',
    'convert.fl-ozToTsp',
    'convert.fl-ozToTbsp',
    'convert.fl-ozToCup',
    'convert.fl-ozToPt',
    'convert.fl-ozToQt',
    'convert.fl-ozToGal',
    'convert.cupToMl',
    'convert.cupToCl',
    'convert.cupToDl',
    'convert.cupToL',
    'convert.cupToTsp',
    'convert.cupToTbsp',
    'convert.cupToFlOz',
    'convert.cupToPt',
    'convert.cupToQt',
    'convert.cupToGal',
    'convert.ptToMl',
    'convert.ptToCl',
    'convert.ptToDl',
    'convert.ptToL',
    'convert.ptToTsp',
    'convert.ptToTbsp',
    'convert.ptToFlOz',
    'convert.ptToCup',
    'convert.ptToQt',
    'convert.ptToGal',
    'convert.qtToMl',
    'convert.qtToCl',
    'convert.qtToDl',
    'convert.qtToL',
    'convert.qtToTsp',
    'convert.qtToTbsp',
    'convert.qtToFlOz',
    'convert.qtToCup',
    'convert.qtToPt',
    'convert.qtToGal',
    'convert.galToMl',
    'convert.galToCl',
    'convert.galToDl',
    'convert.galToL',
    'convert.galToTsp',
    'convert.galToTbsp',
    'convert.galToFlOz',
    'convert.galToCup',
    'convert.galToPt',
    'convert.galToQt',
    'convert.msToS',
    'convert.msToMin',
    'convert.msToH',
    'convert.msToDay',
    'convert.msToWeek',
    'convert.sToMs',
    'convert.sToMin',
    'convert.sToH',
    'convert.sToDay',
    'convert.sToWeek',
    'convert.minToMs',
    'convert.minToS',
    'convert.minToH',
    'convert.minToDay',
    'convert.minToWeek',
    'convert.hToMs',
    'convert.hToS',
    'convert.hToMin',
    'convert.hToDay',
    'convert.hToWeek',
    'convert.dayToMs',
    'convert.dayToS',
    'convert.dayToMin',
    'convert.dayToH',
    'convert.dayToWeek',
    'convert.weekToMs',
    'convert.weekToS',
    'convert.weekToMin',
    'convert.weekToH',
    'convert.weekToDay',
    'convert.mm2ToCm2',
    'convert.mm2ToM2',
    'convert.mm2ToKm2',
    'convert.mm2ToIn2',
    'convert.mm2ToFt2',
    'convert.mm2ToYd2',
    'convert.mm2ToAcre',
    'convert.mm2ToHectare',
    'convert.cm2ToMm2',
    'convert.cm2ToM2',
    'convert.cm2ToKm2',
    'convert.cm2ToIn2',
    'convert.cm2ToFt2',
    'convert.cm2ToYd2',
    'convert.cm2ToAcre',
    'convert.cm2ToHectare',
    'convert.m2ToMm2',
    'convert.m2ToCm2',
    'convert.m2ToKm2',
    'convert.m2ToIn2',
    'convert.m2ToFt2',
    'convert.m2ToYd2',
    'convert.m2ToAcre',
    'convert.m2ToHectare',
    'convert.km2ToMm2',
    'convert.km2ToCm2',
    'convert.km2ToM2',
    'convert.km2ToIn2',
    'convert.km2ToFt2',
    'convert.km2ToYd2',
    'convert.km2ToAcre',
    'convert.km2ToHectare',
    'convert.in2ToMm2',
    'convert.in2ToCm2',
    'convert.in2ToM2',
    'convert.in2ToKm2',
    'convert.in2ToFt2',
    'convert.in2ToYd2',
    'convert.in2ToAcre',
    'convert.in2ToHectare',
    'convert.ft2ToMm2',
    'convert.ft2ToCm2',
    'convert.ft2ToM2',
    'convert.ft2ToKm2',
    'convert.ft2ToIn2',
    'convert.ft2ToYd2',
    'convert.ft2ToAcre',
    'convert.ft2ToHectare',
    'convert.yd2ToMm2',
    'convert.yd2ToCm2',
    'convert.yd2ToM2',
    'convert.yd2ToKm2',
    'convert.yd2ToIn2',
    'convert.yd2ToFt2',
    'convert.yd2ToAcre',
    'convert.yd2ToHectare',
    'convert.acreToMm2',
    'convert.acreToCm2',
    'convert.acreToM2',
    'convert.acreToKm2',
    'convert.acreToIn2',
    'convert.acreToFt2',
    'convert.acreToYd2',
    'convert.acreToHectare',
    'convert.hectareToMm2',
    'convert.hectareToCm2',
    'convert.hectareToM2',
    'convert.hectareToKm2',
    'convert.hectareToIn2',
    'convert.hectareToFt2',
    'convert.hectareToYd2',
    'convert.hectareToAcre',
    'convert.mpsToKm/h',
    'convert.mpsToMph',
    'convert.mpsToKn',
    'convert.mpsToFt/s',
    'convert.kmhToM/s',
    'convert.kmhToMph',
    'convert.kmhToKn',
    'convert.kmhToFt/s',
    'convert.mphToM/s',
    'convert.mphToKm/h',
    'convert.mphToKn',
    'convert.mphToFt/s',
    'convert.knToM/s',
    'convert.knToKm/h',
    'convert.knToMph',
    'convert.knToFt/s',
    'convert.fpsToM/s',
    'convert.fpsToKm/h',
    'convert.fpsToMph',
    'convert.fpsToKn',
    'convert.bToKb',
    'convert.bToMb',
    'convert.bToGb',
    'convert.bToTb',
    'convert.bToPb',
    'convert.kbToB',
    'convert.kbToMb',
    'convert.kbToGb',
    'convert.kbToTb',
    'convert.kbToPb',
    'convert.mbToB',
    'convert.mbToKb',
    'convert.mbToGb',
    'convert.mbToTb',
    'convert.mbToPb',
    'convert.gbToB',
    'convert.gbToKb',
    'convert.gbToMb',
    'convert.gbToTb',
    'convert.gbToPb',
    'convert.tbToB',
    'convert.tbToKb',
    'convert.tbToMb',
    'convert.tbToGb',
    'convert.tbToPb',
    'convert.pbToB',
    'convert.pbToKb',
    'convert.pbToMb',
    'convert.pbToGb',
    'convert.pbToTb',
    'convert.paToKpa',
    'convert.paToBar',
    'convert.paToAtm',
    'convert.paToPsi',
    'convert.paToMmhg',
    'convert.kpaToPa',
    'convert.kpaToBar',
    'convert.kpaToAtm',
    'convert.kpaToPsi',
    'convert.kpaToMmhg',
    'convert.barToPa',
    'convert.barToKpa',
    'convert.barToAtm',
    'convert.barToPsi',
    'convert.barToMmhg',
    'convert.atmToPa',
    'convert.atmToKpa',
    'convert.atmToBar',
    'convert.atmToPsi',
    'convert.atmToMmhg',
    'convert.psiToPa',
    'convert.psiToKpa',
    'convert.psiToBar',
    'convert.psiToAtm',
    'convert.psiToMmhg',
    'convert.mmhgToPa',
    'convert.mmhgToKpa',
    'convert.mmhgToBar',
    'convert.mmhgToAtm',
    'convert.mmhgToPsi',
    'convert.jToKj',
    'convert.jToCal',
    'convert.jToKcal',
    'convert.jToWh',
    'convert.jToKwh',
    'convert.jToBtu',
    'convert.kjToJ',
    'convert.kjToCal',
    'convert.kjToKcal',
    'convert.kjToWh',
    'convert.kjToKwh',
    'convert.kjToBtu',
    'convert.calToJ',
    'convert.calToKj',
    'convert.calToKcal',
    'convert.calToWh',
    'convert.calToKwh',
    'convert.calToBtu',
    'convert.kcalToJ',
    'convert.kcalToKj',
    'convert.kcalToCal',
    'convert.kcalToWh',
    'convert.kcalToKwh',
    'convert.kcalToBtu',
    'convert.whToJ',
    'convert.whToKj',
    'convert.whToCal',
    'convert.whToKcal',
    'convert.whToKwh',
    'convert.whToBtu',
    'convert.kwhToJ',
    'convert.kwhToKj',
    'convert.kwhToCal',
    'convert.kwhToKcal',
    'convert.kwhToWh',
    'convert.kwhToBtu',
    'convert.btuToJ',
    'convert.btuToKj',
    'convert.btuToCal',
    'convert.btuToKcal',
    'convert.btuToWh',
    'convert.btuToKwh',
    'convert.wToKw',
    'convert.wToMw',
    'convert.wToHp',
    'convert.kwToW',
    'convert.kwToMw',
    'convert.kwToHp',
    'convert.mwToW',
    'convert.mwToKw',
    'convert.mwToHp',
    'convert.hpToW',
    'convert.hpToKw',
    'convert.hpToMw',
    'convert.hzToKhz',
    'convert.hzToMhz',
    'convert.hzToGhz',
    'convert.khzToHz',
    'convert.khzToMhz',
    'convert.khzToGhz',
    'convert.mhzToHz',
    'convert.mhzToKhz',
    'convert.mhzToGhz',
    'convert.ghzToHz',
    'convert.ghzToKhz',
    'convert.ghzToMhz',
    'convert.degToRad',
    'convert.degToGrad',
    'convert.degToTurn',
    'convert.radToDeg',
    'convert.radToGrad',
    'convert.radToTurn',
    'convert.gradToDeg',
    'convert.gradToRad',
    'convert.gradToTurn',
    'convert.turnToDeg',
    'convert.turnToRad',
    'convert.turnToGrad',
    'convert.cToF',
    'convert.cToK',
    'convert.fToC',
    'convert.fToK',
    'convert.kToC',
    'convert.kToF',
  ] as const,
  json: [
    'json.jsonParse',
    'json.jsonStringify',
  ] as const,
  time: [
    'time.epochToIso-date',
    'time.iso-dateToEpoch',
  ] as const,
  handler: [
    'handler.retry',
    'handler.fallback',
  ] as const,
  ast: [
    'ast.num',
    'ast.strNode',
    'ast.bool',
    'ast.nil',
    'ast.sym',
    'ast.builtin',
    'ast.effectNode',
    'ast.call',
    'ast.ifNode',
    'ast.block',
    'ast.nodeType',
    'ast.isNum',
    'ast.isStr',
    'ast.isSym',
    'ast.isBuiltin',
    'ast.isCall',
    'ast.isIf',
    'ast.isBlock',
    'ast.isLet',
    'ast.isFn',
    'ast.isBool',
    'ast.isNil',
    'ast.isEffectNode',
    'ast.isAstNode',
    'ast.payload',
    'ast.prettyPrint',
  ] as const,
  macros: [
    'macros.trace',
    'macros.unless',
    'macros.tap',
    'macros.dbg',
    'macros.cond',
  ] as const,
  shorthand: [
    '-short-regexp',
    '-short-fn',
    '-short-object',
  ] as const satisfies `-short-${string}`[],
  datatype: [
    '-type-number',
    '-type-string',
    '-type-object',
    '-type-array',
    '-type-vector',
    '-type-matrix',
    '-type-grid',
    '-type-boolean',
    '-type-atom',
    '-type-function',
    '-type-integer',
    '-type-any',
    '-type-null',
    '-type-collection',
    '-type-sequence',
    '-type-regexp',
    '-type-effect',
    '-type-never',
  ] as const satisfies `-type-${string}`[],
} as const

export type CollectionApiName = typeof api.collection[number]
export type ArrayApiName = typeof api.array[number]
export type SequenceApiName = typeof api.sequence[number]
export type MathApiName = typeof api.math[number]
export type FunctionalApiName = typeof api.functional[number]
export type MiscApiName = typeof api.misc[number]
export type MetaApiName = typeof api.meta[number]
export type ObjectApiName = typeof api.object[number]
export type PredicateApiName = typeof api.predicate[number]
export type RegularExpressionApiName = typeof api.regularExpression[number]
export type SpecialExpressionsApiName = string
export type StringApiName = typeof api.string[number]
export type StringUtilsApiName = typeof api.stringUtils[number]
export type CollectionUtilsApiName = typeof api.collectionUtils[number]
export type SequenceUtilsApiName = typeof api.sequenceUtils[number]
export type BitwiseApiName = typeof api.bitwise[number]
export type BitwiseUtilsApiName = typeof api.bitwiseUtils[number]
export type AssertionApiName = typeof api.assertion[number]
export type AssertionUtilsApiName = typeof api.assertionUtils[number]
export type GridApiName = typeof api.grid[number]
export type MatrixApiName = typeof api.matrix[number]
export type NumberTheoryApiName = typeof api.numberTheory[number]
export type VectorUtilsApiName = typeof api.vectorUtils[number]
export type LinAlgApiName = typeof api.linAlg[number]
export type MathUtilsApiName = typeof api.mathUtils[number]
export type FunctionalUtilsApiName = typeof api.functionalUtils[number]
export type ConvertApiName = typeof api.convert[number]
export type JsonApiName = typeof api.json[number]
export type TimeApiName = typeof api.time[number]
export type HandlerApiName = typeof api.handler[number]
export type AstApiName = typeof api.ast[number]
export type MacrosApiName = typeof api.macros[number]

// Core functions - always available without import
export type CoreNormalExpressionName
  = | CollectionApiName
    | ArrayApiName
    | SequenceApiName
    | MathApiName
    | FunctionalApiName
    | MetaApiName
    | MiscApiName
    | ObjectApiName
    | PredicateApiName
    | RegularExpressionApiName
    | StringApiName
    | BitwiseApiName
    | AssertionApiName

// Module functions - require import()
export type ModuleExpressionName
  = | MatrixApiName
    | VectorUtilsApiName
    | LinAlgApiName
    | GridApiName
    | NumberTheoryApiName
    | MathUtilsApiName
    | FunctionalUtilsApiName
    | AssertionUtilsApiName
    | StringUtilsApiName
    | CollectionUtilsApiName
    | SequenceUtilsApiName
    | BitwiseUtilsApiName
    | ConvertApiName
    | JsonApiName
    | TimeApiName
    | HandlerApiName
    | AstApiName
    | MacrosApiName
export type NormalExpressionName
  = | CoreNormalExpressionName
    | ModuleExpressionName

export type FunctionName
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  = | NormalExpressionName
    | SpecialExpressionsApiName

export type ShorthandName = typeof api.shorthand[number]

export type DatatypeName = typeof api.datatype[number]

// Core API function names (always available)
const coreApiFunctionNames = [
  ...api.collection,
  ...api.array,
  ...api.sequence,
  ...api.math,
  ...api.functional,
  ...api.meta,
  ...api.misc,
  ...api.object,
  ...api.predicate,
  ...api.regularExpression,
  ...api.string,
  ...api.bitwise,
  ...api.assertion,
] as const

// Module API function names (require import())
const moduleApiFunctionNames = [
  ...api.matrix,
  ...api.vectorUtils,
  ...api.linAlg,
  ...api.grid,
  ...api.numberTheory,
  ...api.mathUtils,
  ...api.functionalUtils,
  ...api.assertionUtils,
  ...api.stringUtils,
  ...api.collectionUtils,
  ...api.sequenceUtils,
  ...api.bitwiseUtils,
  ...api.convert,
  ...api.json,
  ...api.time,
] as const
const apiFunctionNames = [
  ...coreApiFunctionNames,
  ...moduleApiFunctionNames,
] as const

// Core API names (core functions + shorthand + datatype)
const coreApiNames = [
  ...coreApiFunctionNames,
  ...api.shorthand,
  ...api.datatype,
] as const

const apiNames = [
  ...apiFunctionNames,
  ...api.shorthand,
  ...api.datatype,
] as const

export type CoreApiName = typeof coreApiNames[number]
export type ApiName = typeof apiNames[number]

export function isApiName(arg: string): arg is ApiName {
  return apiNames.includes(arg as ApiName)
}

export function isCoreApiName(arg: string): arg is CoreApiName {
  return coreApiNames.includes(arg as CoreApiName)
}
